import React, { useState, useEffect, memo } from 'react';
import { Modal, Button, Input, DatePicker, Checkbox, Row, Col, Typography, message, Table, Select, Space, Upload } from 'antd';
import { CloseOutlined, DeleteOutlined, PlusOutlined, ScanOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import axiosInstance from '../api/axios';
import { useLaboratoryId } from '../hooks/useLaboratoryId';
import { createStandard } from '../api/chemicalRegistry';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd';
import Scan from './Scan';
import * as XLSX from 'xlsx';
import { getCustomColumns, CustomColumn } from '../api/customColumns';

const { Text, Link } = Typography;
const { Dragger } = Upload;

const StyledButton = styled(Button)`
  height: 40px;
  width: 150px;
  border-radius: 4px;
`;

const ButtonsRow = styled(Row)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Обновляем интерфейс DataType
interface DataType {
  key: React.Key;
  number: number;
  name: string;
  manufacturer: string;
  purpose: string;
  batchCode: string;
  packaging: string;
  indicator: string;
  certifiedValue: number;
  certifiedValueUnit: string;
  errorValue: number;
  errorValueUnit: string;
  additionalInfo: string;
  normativeDocument: { name: string; file: File | null };
  expirationDate: dayjs.Dayjs;
  expirationNotification: string;
  releaseDate: dayjs.Dayjs;
  notes: string;
  isPrecursor: boolean;
  // Добавляем поля хранения
  storageLocation: string;
  storageConditions: string;
  openedShelfLife: string;
  openedDate: dayjs.Dayjs | null;
  // Добавляем поля учета
  used: string;
  remaining: string;
  minimumRemaining: string;
  custom_columns?: Record<string, any>;
}

// Явно указываем тип данных для StyledTable
const StyledTable = styled((props: TableProps<DataType>) => <Table<DataType> {...props} />)`
  .ant-table-tbody > tr > td {
    height: 55px;
    padding: 8px;
  }
  
  .ant-table-body {
    height: 260px !important;
    overflow-y: auto !important;
  }
`;

interface MultipleStandardSamplesAddProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

// Компоненты для ячеек таблицы
const CellInput = memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%' }} />
));

const CellNumberInput = memo(({ value, onChange }: { value: number, onChange: (value: number) => void }) => (
  <Input
    type="number"
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    style={{ width: '100%' }}
  />
));

const CellSelect = memo(({ value, onChange, options }: { value: string, onChange: (value: string) => void, options: { label: string, value: string }[] }) => (
  <Select
    value={value}
    onChange={onChange}
    options={options}
    style={{ width: '100%' }}
    showSearch
    allowClear
  />
));

const CellDatePicker = memo(({ value, onChange }: { value: dayjs.Dayjs | null, onChange: (value: dayjs.Dayjs | null) => void }) => (
  <DatePicker
    value={value}
    onChange={onChange}
    format="DD.MM.YYYY"
    style={{ width: '100%' }}
  />
));

const CellCheckbox = memo(({ checked, onChange }: { checked: boolean, onChange: (checked: boolean) => void }) => (
  <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
));

// Добавляем новый компонент для ячейки с загрузкой файла
const CellUpload = memo(({ value, onChange }: { value: { name: string, file: File | null }, onChange: (value: { name: string, file: File | null }) => void }) => (
  <Row gutter={8} align="middle">
    <Col flex="auto">
      <Input
        value={value.name || 'Файл не выбран'}
        readOnly
        placeholder="Файл не выбран"
      />
    </Col>
    <Col>
      <Upload
        accept=".pdf,.doc,.docx,.txt"
        showUploadList={false}
        beforeUpload={(file) => {
          const maxSize = 20 * 1024 * 1024; // 20MB
          if (file.size > maxSize) {
            message.error('Размер файла не должен превышать 20MB');
            return Upload.LIST_IGNORE;
          }
          onChange({ name: file.name, file });
          return false;
        }}
      >
        <Button icon={<UploadOutlined />}/>
      </Upload>
    </Col>
    {value.name && (
      <Col>
        <Button
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onChange({ name: '', file: null });
          }}
        />
      </Col>
    )}
  </Row>
));

// Опции для селектов
const standardTypes = [
  'ГСО 1234-56 Стандартный образец 1',
  'ОСО 7890-12 Стандартный образец 2',
  'СОП 3456-78 Стандартный образец 3',
].map(item => ({ label: item, value: item }));

const manufacturerOptions = [
  'ФГУП "ВНИИМ им. Д.И. Менделеева"',
  'ФГУП "УНИИМ"',
  'ООО "Химтест"',
].map(item => ({ label: item, value: item }));

const purposeOptions = [
  'Калибровка',
  'Поверка',
  'Контроль точности',
].map(item => ({ label: item, value: item }));

const indicatorOptions = [
  'Массовая доля железа',
  'Массовая доля углерода',
  'Массовая доля серы',
].map(item => ({ label: item, value: item }));

const notificationOptions = [
  '1 месяц',
  '3 месяца',
  '6 месяцев',
].map(item => ({ label: item, value: item }));

const MultipleStandardSamplesAdd: React.FC<MultipleStandardSamplesAddProps> = ({
  open,
  onCancel,
  onSuccess
}) => {
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const labId = useLaboratoryId();
  const [scanModalVisible, setScanModalVisible] = useState<boolean>(false);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);

  // Загрузка кастомных колонок при монтировании
  useEffect(() => {
    const fetchCustomColumns = async () => {
      if (!labId) return;
      
      try {
        const columns = await getCustomColumns('standard', Number(labId));
        setCustomColumns(columns);
      } catch (error) {
        console.error('Ошибка при загрузке кастомных колонок:', error);
      }
    };
    
    fetchCustomColumns();
  }, [labId]);

  // Очистка данных при размонтировании компонента
  useEffect(() => {
    return () => {
      setDataSource([]);
    };
  }, []);

  // Обработчик для нового сканирования
  const handleNewScan = async (scannedData: string) => {
    try {
      setScanModalVisible(false);
      const parsedData = JSON.parse(scannedData);
      
      if (!parsedData.batch_id) {
        message.error('Некорректные данные QR-кода');
        return;
      }

      setLoading(true);
      const response = await axiosInstance.get(`/api/batches/${parsedData.batch_id}`);
      const batchData = response.data;
      
      const certifiedValues = batchData.certified_values || [];
      
      if (certifiedValues.length === 0) {
        const newRow: DataType = {
          key: Date.now().toString(),
          number: dataSource.length + 1,
          name: batchData.so_name + ', ' + batchData.gso_number + ', ' + batchData.so_category || '',
          manufacturer: 'ООО "Спектрохим"',
          purpose: '-',
          batchCode: batchData.batch_code || '',
          packaging: batchData.packaging || '',
          indicator: batchData.indicator_name || '',
          certifiedValue: batchData.target_value || 0,
          certifiedValueUnit: batchData.target_unit || '',
          errorValue: 0,
          errorValueUnit: '',
          additionalInfo: '',
          normativeDocument: { name: '', file: null },
          expirationDate: batchData.expiry_date ? dayjs(batchData.expiry_date) : dayjs(),
          expirationNotification: '',
          releaseDate: batchData.registration_date ? dayjs(batchData.registration_date) : dayjs(),
          notes: '',
          isPrecursor: false,
          storageLocation: '',
          storageConditions: '',
          openedShelfLife: '',
          openedDate: null,
          used: '0',
          remaining: '',
          minimumRemaining: '10',
          custom_columns: {}
        };
        
        setDataSource(prev => [...prev, newRow]);
      } else {
        const rows = certifiedValues.map((value: any, index: number) => ({
          key: `${Date.now()}-${index}`,
          number: dataSource.length + index + 1,
          name: batchData.so_name + ', ' + batchData.gso_number + ', ' + batchData.so_category || '',
          manufacturer: 'ООО "Спектрохим"',
          purpose: '-',
          batchCode: batchData.batch_code || '',
          packaging: batchData.packaging || '',
          indicator: value.indicator_name || batchData.indicator_name || '',
          certifiedValue: value.value || 0,
          certifiedValueUnit: value.unit || batchData.target_unit || '',
          errorValue: value.error || 0,
          errorValueUnit: value.error_unit || '',
          additionalInfo: '',
          normativeDocument: { name: '', file: null },
          expirationDate: batchData.expiry_date ? dayjs(batchData.expiry_date) : dayjs(),
          expirationNotification: '',
          releaseDate: batchData.registration_date ? dayjs(batchData.registration_date) : dayjs(),
          notes: '',
          isPrecursor: false,
          storageLocation: '',
          storageConditions: '',
          openedShelfLife: '',
          openedDate: null,
          used: '0',
          remaining: '',
          minimumRemaining: '10',
          custom_columns: {}
        }));
        
        setDataSource(prev => [...prev, ...rows]);
      }
      
      updateRowNumbers();
      message.success('Данные успешно добавлены');
    } catch (error) {
      console.error('Ошибка при обработке отсканированных данных:', error);
      message.error('Ошибка при обработке отсканированных данных');
    } finally {
      setLoading(false);
    }
  };

  // Функция для обновления номеров строк
  const updateRowNumbers = () => {
    setDataSource(prev => prev.map((item, index) => ({
      ...item,
      number: index + 1
    })));
  };

  const handleInputChange = (key: React.Key, field: string | string[], value: any) => {
    setDataSource(prev => prev.map(item => {
      if (item.key === key) {
        if (Array.isArray(field)) {
          const newItem = { ...item };
          let current: any = newItem;
          for (let i = 0; i < field.length - 1; i++) {
            current = current[field[i]];
          }
          current[field[field.length - 1]] = value;
          return newItem;
        }
        
        return {
          ...item,
          [field]: value
        };
      }
      return item;
    }));
  };

  const handleAddRow = () => {
    const newKey = Date.now().toString();
    const newRow: DataType = {
      key: newKey,
      number: dataSource.length + 1,
      name: '',
      manufacturer: 'ООО "Спектрохим"',
      purpose: '',
      batchCode: '',
      packaging: '',
      indicator: '',
      certifiedValue: 0,
      certifiedValueUnit: '',
      errorValue: 0,
      errorValueUnit: '',
      additionalInfo: '',
      normativeDocument: { name: '', file: null },
      expirationDate: dayjs(),
      expirationNotification: '',
      releaseDate: dayjs(),
      notes: '',
      isPrecursor: false,
      storageLocation: '',
      storageConditions: '',
      openedShelfLife: '',
      openedDate: null,
      used: '0',
      remaining: '',
      minimumRemaining: '10',
      custom_columns: {}
    };
    
    setDataSource([...dataSource, newRow]);
  };

  const handleDelete = (key: React.Key) => {
    setDataSource(prev => prev.filter(item => item.key !== key));
    setTimeout(updateRowNumbers, 0);
  };

  // Обработчик для импорта из Excel
  const handleExcelImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData: any[] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        // Пропускаем заголовок и обрабатываем данные
        const newRows: DataType[] = jsonData.slice(1).map((row: any[], index: number) => ({
          key: `import-${Date.now()}-${index}`,
          number: dataSource.length + index + 1,
          name: row[0] || '',
          manufacturer: row[1] || 'ООО "Спектрохим"',
          purpose: row[2] || '',
          batchCode: row[3] || '',
          packaging: row[4] || '',
          indicator: row[5] || '',
          certifiedValue: parseFloat(row[6]) || 0,
          certifiedValueUnit: row[7] || '',
          errorValue: parseFloat(row[8]) || 0,
          errorValueUnit: row[9] || '',
          additionalInfo: row[10] || '',
          normativeDocument: { name: '', file: null },
          expirationDate: row[12] ? dayjs(row[12]) : dayjs(),
          expirationNotification: row[13] || '',
          releaseDate: row[14] ? dayjs(row[14]) : dayjs(),
          notes: row[15] || '',
          isPrecursor: Boolean(row[16]),
          storageLocation: '',
          storageConditions: '',
          openedShelfLife: '',
          openedDate: null,
          used: '0',
          remaining: '',
          minimumRemaining: '10',
          custom_columns: {}
        }));

        setDataSource(prev => [...prev, ...newRows]);
        updateRowNumbers();
        message.success('Данные успешно импортированы');
      } catch (error) {
        console.error('Ошибка при импорте Excel:', error);
        message.error('Ошибка при импорте данных из Excel');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Предотвращаем стандартную загрузку файла
  };

  // Обработчик для экспорта шаблона Excel
  const handleExportTemplate = () => {
    const headers = [
      'Наименование',
      'Производитель',
      'Назначение',
      'Шифр партии',
      'Фасовка',
      'Показатель',
      'Аттестованное значение',
      'Единица измерения АЗ',
      'Погрешность',
      'Единица измерения погрешности',
      'Доп. сведения',
      'Нормативный документ',
      'Срок годности',
      'Уведомление',
      'Дата выпуска',
      'Примечание',
      'Прекурсор',
      'Место хранения',
      'Условия хранения',
      'Срок хранения после вскрытия (дней)',
      'Дата вскрытия',
      'Использовано',
      'Остаток',
      'Минимальный остаток'
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Шаблон');
    XLSX.writeFile(wb, 'template_standard_samples.xlsx');
  };

  const columns: ColumnsType<DataType> = [
    {
      title: '№ п/п',
      dataIndex: 'number',
      key: 'number',
      width: 80,
    },
    {
      title: 'Наименование, тип, номер и категория СО',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'name', newValue)} />
      ),
    },
    {
      title: 'Изготовитель СО',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'manufacturer', newValue)} />
      ),
    },
    {
      title: 'Назначение',
      dataIndex: 'purpose',
      key: 'purpose',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'purpose', newValue)} />
      ),
    },
    {
      title: 'Шифр партии',
      dataIndex: 'batchCode',
      key: 'batchCode',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'batchCode', newValue)} />
      ),
    },
    {
      title: 'Фасовка',
      dataIndex: 'packaging',
      key: 'packaging',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'packaging', newValue)} />
      ),
    },
    {
      title: 'Показатель',
      dataIndex: 'indicator',
      key: 'indicator',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'indicator', newValue)} />
      ),
    },
    {
      title: 'Аттестов. значение м.д. %',
      dataIndex: 'certifiedValue',
      key: 'certifiedValue',
      width: 200,
      render: (value: number, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'certifiedValue', newValue)} />
      ),
    },
    {
      title: 'Единица измерения атт. значения',
      dataIndex: 'certifiedValueUnit',
      key: 'certifiedValueUnit',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'certifiedValueUnit', newValue)} />
      ),
    },
    {
      title: 'Погрешность а.з. абс. %',
      dataIndex: 'errorValue',
      key: 'errorValue',
      width: 200,
      render: (value: number, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'errorValue', newValue)} />
      ),
    },
    {
      title: 'Единица измерения погрешности',
      dataIndex: 'errorValueUnit',
      key: 'errorValueUnit',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'errorValueUnit', newValue)} />
      ),
    },
    {
      title: 'Доп сведения',
      dataIndex: 'additionalInfo',
      key: 'additionalInfo',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'additionalInfo', newValue)} />
      ),
    },
    {
      title: 'Нормативный документ',
      dataIndex: 'normativeDocument',
      key: 'normativeDocument',
      width: 300,
      render: (value: { name: string; file: File | null }, record: DataType) => (
        <CellUpload
          value={value}
          onChange={(newValue) => handleInputChange(record.key, 'normativeDocument', newValue)}
        />
      ),
    },
    {
      title: 'Срок годности экземпляра СО',
      dataIndex: 'expirationDate',
      key: 'expirationDate',
      width: 200,
      render: (value: dayjs.Dayjs, record: DataType) => (
        <CellDatePicker 
          value={value || null} 
          onChange={(date) => handleInputChange(record.key, 'expirationDate', date || dayjs())} 
        />
      ),
    },
    {
      title: 'Уведомление об окончании срока годности',
      dataIndex: 'expirationNotification',
      key: 'expirationNotification',
      width: 200,
      render: (value: string, record: DataType) => (
        <Select
          value={value}
          onChange={(newValue) => handleInputChange(record.key, 'expirationNotification', newValue)}
          options={notificationOptions}
          style={{ width: '100%' }}
          showSearch
          allowClear
        />
      ),
    },
    {
      title: 'Дата выпуска экземпляра СО',
      dataIndex: 'releaseDate',
      key: 'releaseDate',
      width: 200,
      render: (value: dayjs.Dayjs, record: DataType) => (
        <CellDatePicker 
          value={value || null} 
          onChange={(date) => handleInputChange(record.key, 'releaseDate', date || dayjs())} 
        />
      ),
    },
    {
      title: 'Примечание',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'notes', newValue)} />
      ),
    },
    {
      title: 'Прекурсор',
      dataIndex: 'isPrecursor',
      key: 'isPrecursor',
      width: 120,
      render: (value: boolean, record: DataType) => (
        <CellCheckbox checked={value} onChange={(checked) => handleInputChange(record.key, 'isPrecursor', checked)} />
      ),
    },
    {
      title: 'Место хранения',
      dataIndex: 'storageLocation',
      key: 'storageLocation',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'storageLocation', newValue)} />
      ),
    },
    {
      title: 'Условия хранения',
      dataIndex: 'storageConditions',
      key: 'storageConditions',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'storageConditions', newValue)} />
      ),
    },
    {
      title: 'Срок хранения после вскрытия (дней)',
      dataIndex: 'openedShelfLife',
      key: 'openedShelfLife',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput 
          value={value} 
          onChange={(newValue) => handleInputChange(record.key, 'openedShelfLife', newValue)} 
        />
      ),
    },
    {
      title: 'Дата вскрытия',
      dataIndex: 'openedDate',
      key: 'openedDate',
      width: 200,
      render: (value: dayjs.Dayjs | null, record: DataType) => (
        <CellDatePicker 
          value={value} 
          onChange={(date) => handleInputChange(record.key, 'openedDate', date)} 
        />
      ),
    },
    {
      title: 'Использовано',
      dataIndex: 'used',
      key: 'used',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput 
          value={value} 
          onChange={(newValue) => handleInputChange(record.key, 'used', newValue)} 
        />
      ),
    },
    {
      title: 'Остаток',
      dataIndex: 'remaining',
      key: 'remaining',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput 
          value={value} 
          onChange={(newValue) => handleInputChange(record.key, 'remaining', newValue)} 
        />
      ),
    },
    {
      title: 'Минимальный остаток',
      dataIndex: 'minimumRemaining',
      key: 'minimumRemaining',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput 
          value={value} 
          onChange={(newValue) => handleInputChange(record.key, 'minimumRemaining', newValue)} 
        />
      ),
    },
    // Добавляем кастомные колонки
    ...customColumns.map(column => ({
      title: column.title,
      dataIndex: ['custom_columns', column.key],
      key: column.key,
      width: 200,
      render: (value: any, record: DataType) => {
        switch (column.type) {
          case 'text':
            return (
              <CellInput
                value={value || ''}
                onChange={(newValue) => handleInputChange(record.key, ['custom_columns', column.key], newValue)}
              />
            );
          case 'int':
            return (
              <CellNumberInput
                value={value || 0}
                onChange={(newValue) => handleInputChange(record.key, ['custom_columns', column.key], newValue)}
              />
            );
          case 'date':
            return (
              <CellDatePicker
                value={value ? dayjs(value) : null}
                onChange={(date) => handleInputChange(record.key, ['custom_columns', column.key], date?.toISOString() || null)}
              />
            );
          case 'boolean':
            return (
              <CellCheckbox
                checked={Boolean(value)}
                onChange={(checked) => handleInputChange(record.key, ['custom_columns', column.key], checked)}
              />
            );
          default:
            return null;
        }
      }
    })),
    {
      key: "action",
      width: 60,
      render: (_: unknown, record: DataType) => (
        <Button 
          type="text" 
          icon={<DeleteOutlined style={{ color: '#f5222d' }} />}
          style={{ width: '32px', padding: 0 }}
          onClick={() => handleDelete(record.key)}
        />
      ),
    },
  ];

  const handleSave = async () => {
    try {
      if (!labId) {
        message.error('ID лаборатории не найден');
        return;
      }
      
      if (dataSource.length === 0) {
        message.error('Нет данных для сохранения');
        return;
      }
      
      setLoading(true);
      
      // Сохраняем все строки таблицы как стандартные образцы
      for (const item of dataSource) {
        const formData = new FormData();
        
        const formattedValues = {
          name: item.name,
          manufacturer: item.manufacturer,
          purpose: item.purpose,
          batch_code: item.batchCode,
          packaging: item.packaging,
          indicator: item.indicator,
          certified_value: item.certifiedValue,
          certified_value_unit: item.certifiedValueUnit,
          error_value: item.errorValue,
          error_value_unit: item.errorValueUnit,
          additional_info: item.additionalInfo,
          normative_document: item.normativeDocument.name,
          expiration_date: item.expirationDate ? item.expirationDate.format('YYYY-MM-DD') : null,
          expiration_notification: item.expirationNotification,
          release_date: item.releaseDate ? item.releaseDate.format('YYYY-MM-DD') : null,
          notes: item.notes,
          is_precursor: item.isPrecursor,
          storage_location: item.storageLocation,
          storage_conditions: item.storageConditions,
          opened_shelf_life: item.openedShelfLife,
          opened_date: item.openedDate ? item.openedDate.format('YYYY-MM-DD') : null,
          used: item.used,
          remaining: item.remaining,
          minimum_remaining: item.minimumRemaining,
          custom_columns: item.custom_columns || {}
        };

        formData.append('standard_data', JSON.stringify(formattedValues));
        
        // Добавляем файл, если он есть
        if (item.normativeDocument.file) {
          formData.append('normative_document', item.normativeDocument.file);
        }
        
        await createStandard(labId, formData);
      }
      
      message.success(`${dataSource.length} стандартных образцов успешно добавлено`);
      if (onSuccess) {
        onSuccess();
      }
      onCancel();
    } catch (error) {
      console.error('Ошибка при сохранении стандартных образцов:', error);
      message.error('Не удалось сохранить стандартные образцы');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="Добавление множества позиций"
        open={open}
        onCancel={onCancel}
        width={1200}
        footer={null}
        closeIcon={<CloseOutlined style={{ color: '#8C8C8C' }} />}
        centered
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={24}>
              <Space>
                <Button 
                    type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAddRow}
                >
                  Добавить строку
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        
        <StyledTable
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          scroll={{ x: 'max-content', y: 300 }}
          loading={loading}
        />
        
        <ButtonsRow justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Typography.Text style={{ marginRight: 16, opacity: 0.5 }}>
              Добавлено {dataSource.length} позиций
            </Typography.Text>
            <StyledButton onClick={onCancel}>
              Отмена
            </StyledButton>
            <StyledButton type="primary" onClick={handleSave} disabled={dataSource.length === 0}>
              Сохранить
            </StyledButton>
          </Space>
        </ButtonsRow>
      </Modal>
      
      <Scan 
        open={scanModalVisible} 
        onCancel={() => setScanModalVisible(false)} 
        onScan={handleNewScan} 
      />
    </>
  );
};

export default MultipleStandardSamplesAdd; 